/**
 * SF26 — वास्तविक मालिक कार्य अनुबंध और काम पूरा होने का प्रमाण
 *
 * यह मॉड्यूल Smart Shikka असिस्टेंट के लिए "क्या काम कर सकता है"
 * और "कब काम पूरा माना जाएगा" का साफ अनुबंध परिभाषित करता है।
 * इसमें कोई real live automation नहीं है।
 */
// ─── १० वास्तविक मालिक workflows ────────────────────────
const OWNER_WORKFLOWS = Object.freeze([
    Object.freeze({
        id: 'wf-01-voice-command-plan',
        name: 'आवाज से आदेश लेना और text plan बनाना',
        category: 'voice',
        input: 'voice audio / transcript',
        allowedTools: ['transcription', 'text_plan_generator'],
        approvalRequired: true,
        riskLevel: 'medium',
        rollbackPolicy: 'plan discard — कोई side effect नहीं',
        successEvidence: 'transcript + generated plan text + user confirmation',
        refusalCondition: 'audio quality < threshold या language unsupported',
    }),
    Object.freeze({
        id: 'wf-02-youtube-summary',
        name: 'YouTube/video देखकर summary, action list, समय-सूची और सीखने योग्य points निकालना',
        category: 'video',
        input: 'YouTube URL / video file reference',
        allowedTools: ['transcript_fetcher', 'summary_generator', 'timeline_extractor', 'frame_sampler'],
        approvalRequired: false,
        riskLevel: 'low',
        rollbackPolicy: 'read-only — कोई rollback जरूरी नहीं, frame sampling max 10',
        successEvidence: 'transcript + summary + action items + timestamps',
        refusalCondition: 'age-restricted / private / paywall video',
    }),
    Object.freeze({
        id: 'wf-03-browser-read-form',
        name: 'browser page पढ़ना, form पहचानना, submit/click से पहले approval लेना',
        category: 'browser',
        input: 'URL / page reference',
        allowedTools: ['page_reader', 'form_detector', 'field_redactor'],
        approvalRequired: true,
        riskLevel: 'high',
        rollbackPolicy: 'कोई submit नहीं — approval बिना कोई action नहीं',
        successEvidence: 'page content + form fields identified + approval request sent',
        refusalCondition: 'login required / payment page / sensitive domain',
    }),
    Object.freeze({
        id: 'wf-04-file-read-diff-write',
        name: 'local/project files पढ़ना, diff बनाना, backup लेकर लिखना',
        category: 'file',
        input: 'file path / project reference',
        allowedTools: ['file_reader', 'diff_generator', 'backup_creator', 'sandbox_writer'],
        approvalRequired: true,
        riskLevel: 'high',
        rollbackPolicy: 'backup से restore — हर write से पहले backup mandatory',
        successEvidence: 'diff preview + backup path + write confirmation + rollback proof',
        refusalCondition: 'forbidden path (/, /etc, .ssh, .env, *.key, *.pem)',
    }),
    Object.freeze({
        id: 'wf-05-code-review',
        name: 'code review करना, patch plan बनाना, tests suggest करना',
        category: 'code',
        input: 'code file / PR diff / repository reference',
        allowedTools: ['code_analyzer', 'patch_planner', 'test_suggester'],
        approvalRequired: false,
        riskLevel: 'low',
        rollbackPolicy: 'read-only analysis — कोई rollback जरूरी नहीं',
        successEvidence: 'review comments + patch plan + suggested tests',
        refusalCondition: 'binary file / encrypted file / > size limit',
    }),
    Object.freeze({
        id: 'wf-06-smart-print-readonly',
        name: 'Smart Print या business workflow में read-only data समझना',
        category: 'business',
        input: 'business document / invoice / report reference',
        allowedTools: ['document_reader', 'data_summarizer'],
        approvalRequired: false,
        riskLevel: 'low',
        rollbackPolicy: 'read-only — कोई rollback जरूरी नहीं',
        successEvidence: 'parsed data + summary + key fields extracted',
        refusalCondition: 'encrypted document / access denied',
    }),
    Object.freeze({
        id: 'wf-07-memory-crud',
        name: 'user memory देखना/edit/delete करना',
        category: 'memory',
        input: 'memory query / edit request / delete request',
        allowedTools: ['memory_viewer', 'memory_editor', 'memory_deleter'],
        approvalRequired: true,
        riskLevel: 'medium',
        rollbackPolicy: 'delete proof + edit history — restore possible',
        successEvidence: 'memory state before + action taken + state after + proof',
        refusalCondition: 'system memory / protected memory scope',
    }),
    Object.freeze({
        id: 'wf-08-budget-stop',
        name: 'खर्च/token limit रोकना',
        category: 'budget',
        input: 'budget threshold / token limit / cost policy',
        allowedTools: ['budget_monitor', 'token_counter', 'hard_stop_trigger'],
        approvalRequired: false,
        riskLevel: 'low',
        rollbackPolicy: 'automatic stop — कोई rollback जरूरी नहीं',
        successEvidence: 'budget status + stop trigger point + remaining allowance',
        refusalCondition: 'N/A — always active',
    }),
    Object.freeze({
        id: 'wf-09-failure-rollback',
        name: 'failed action पर rollback/stop करना',
        category: 'recovery',
        input: 'failed action reference / error state',
        allowedTools: ['rollback_executor', 'state_restorer', 'audit_logger'],
        approvalRequired: false,
        riskLevel: 'medium',
        rollbackPolicy: 'automatic rollback to last known good state',
        successEvidence: 'rollback confirmation + restored state proof + audit entry',
        refusalCondition: 'no backup available / corrupted state',
    }),
    Object.freeze({
        id: 'wf-10-evidence-answer',
        name: 'final answer में evidence देना: क्या पढ़ा, क्या किया, क्या नहीं किया',
        category: 'evidence',
        input: 'completed workflow chain / session history',
        allowedTools: ['evidence_collector', 'answer_formatter', 'audit_summarizer'],
        approvalRequired: false,
        riskLevel: 'low',
        rollbackPolicy: 'read-only summary — कोई rollback जरूरी नहीं',
        successEvidence: 'structured evidence: sources read, actions taken, actions refused, cost, audit trail',
        refusalCondition: 'N/A — always produces evidence',
    }),
]);
// ─── अनुबंध ──────────────────────────────────────────────
export const REAL_WORK_CONTRACT_SF26 = Object.freeze({
    version: '0.1.95',
    pr: 'SF26',
    workflows: OWNER_WORKFLOWS,
    totalWorkflows: OWNER_WORKFLOWS.length,
    noAutonomousProductionAction: true,
    ownerApprovalRequired: true,
    auditRequired: true,
});
//# sourceMappingURL=realWorkContractSf26.js.map